import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TYPES_ENTREE_CRISE } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
export function JournalCrise({ id, statut }: { id: string; statut: string }) {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const derniere = useRef<{ empreinte: string; id: string } | null>(null);
  const q = useQuery({
    queryKey: ['journal-crise', id],
    queryFn: () =>
      requete<{
        elements: {
          id: string;
          type: keyof typeof TYPES_ENTREE_CRISE;
          contenu: string;
          auteur: string;
          cree_le: string;
        }[];
      }>(`/incidents/${id}/journal-crise`),
    enabled: peut(contexte, 'crise', 'voir'),
  });
  if (!peut(contexte, 'crise', 'voir')) return null;
  return (
    <section aria-label="Journal de crise">
      <h2>Journal de crise</h2>
      <p>
        Notes, moyens et décisions conservés sans modification. Une correction s’ajoute comme une
        nouvelle entrée. Les 500 premières entrées sont affichées.
      </p>
      {(erreur || q.error) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {peut(contexte, 'incident', 'exporter') && (
        <div>
          <p>
            Après rétablissement vérifié, téléchargez le rapport de fin de crise et imprimez-le en
            PDF depuis votre navigateur.
          </p>
          <button
            disabled={occupe || !['RESTORED_VERIFIED', 'CLOSED'].includes(statut)}
            onClick={async () => {
              setOccupe(true);
              setErreur('');
              try {
                const r = await requete<Response>(`/incidents/${id}/rapport-crise`, { brut: true });
                const url = URL.createObjectURL(await r.blob());
                const a = document.createElement('a');
                a.href = url;
                a.download = `rapport-crise-${id}.html`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch (e) {
                setErreur(messageErreur(e));
              } finally {
                setOccupe(false);
              }
            }}
          >
            Télécharger le rapport de fin de crise
          </button>
        </div>
      )}
      {peut(contexte, 'crise', 'creer') && (
        <form
          style={{ display: 'grid', gap: 12, maxWidth: 750 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const f = new FormData(form);
            const corps = { type: f.get('type'), contenu: f.get('contenu') };
            const empreinte = JSON.stringify(corps);
            if (derniere.current?.empreinte !== empreinte)
              derniere.current = { empreinte, id: crypto.randomUUID() };
            setErreur('');
            setOccupe(true);
            try {
              await requete(`/incidents/${id}/journal-crise`, {
                methode: 'POST',
                corps: { ...corps, requete_id: derniere.current.id },
              });
              await client.invalidateQueries({ queryKey: ['journal-crise', id] });
              form.reset();
              derniere.current = null;
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setOccupe(false);
            }
          }}
        >
          <label>
            Type d’entrée de crise{' '}
            <select name="type" defaultValue="NOTE">
              {Object.entries(TYPES_ENTREE_CRISE)
                .filter(([k]) => k !== 'DECISION' || peut(contexte, 'crise', 'decider'))
                .map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Contenu du journal <textarea name="contenu" required minLength={3} maxLength={2000} />
          </label>
          <button disabled={occupe}>Ajouter au journal de crise</button>
        </form>
      )}
      {q.isPending && <p>Chargement du journal…</p>}
      <ol>
        {q.data?.elements.map((e) => (
          <li key={e.id}>
            <strong>{TYPES_ENTREE_CRISE[e.type]}</strong> · {e.auteur} ·{' '}
            {new Date(e.cree_le).toLocaleString('fr-FR')}
            <p style={{ whiteSpace: 'pre-wrap' }}>{e.contenu}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
